import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import BankMutationInput from "./BankMutationInput/BankMutationInput";
import { Props, State } from "./interfaces";
import { AddEditButton } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";

import { EmployeeBankAccountMutationSTO } from "../../../../models/employee-bank-accounts/mutations/employee-bank-account-mutation-sto";
import { EmployeeBankAccountMutationsApiService } from "../../../../services/employees/employees-bank-account-mutations-api.service";
import { EmployeeCurrentBankAccountMutationsApiService } from "../../../../services/employees/employees-current-bank-account-mutations-api.service";

import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";

import { EmployeeDataContext } from "../../EmployeeDataAndMutaions";

const AddEditBankAccountMutation = ({ original, mutation, hasMutation, handleReload }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path

  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    original: null,
    mutation: null,
    resetMutation: null,
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (!original || !mutation) return;

    setState((state) => ({
      ...state,
      original,
      mutation,
      resetMutation: mutation,
    }));
  }, [original, mutation]);

  const setIsDialogOpen = (value: boolean) => setState((state) => ({ ...state, isDialogOpen: value }));

  const setIsConfirmationChangesDialogOpen = (value: boolean) =>
    setState((state) => ({ ...state, isConfirmationChangesDialogOpen: value }));

  const closeMainDialog = () => {
    if (!isEqual(state.mutation, state.resetMutation)) {
      setIsConfirmationChangesDialogOpen(true);
    } else setIsDialogOpen(false);
  };

  const handleDiscardChanges = () =>
    setState((state) => ({
      ...state,
      mutation,
      resetMutation: mutation,
      isDialogOpen: false,
      isConfirmationChangesDialogOpen: false,
      error: "",
    }));

  const handleSave = async (): Promise<void> => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const apiService = forManager
        ? new EmployeeBankAccountMutationsApiService()
        : new EmployeeCurrentBankAccountMutationsApiService();

      if (forManager) {
        apiService.useSaveItemsPath("/ApproveEmployeeBankAccountMutations");
      }

      const response = await apiService.saveItems([...[state.mutation as EmployeeBankAccountMutationSTO]]);

      toastifyResponse(response, "all", "default_success_message", "failure_saving_data", "error_saving_data");

      setState((state) => ({
        ...state,
        loading: false,
        isDialogOpen: false,
      }));

      handleReload();
    } catch (error: any) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error?.message ?? error,
      }));
    }
  };

  const handleChange = (
    field: keyof EmployeeBankAccountMutationSTO,
    value: keyof EmployeeBankAccountMutationSTO[keyof EmployeeBankAccountMutationSTO]
  ) => {
    setState((state) => ({
      ...state,
      mutation: {
        ...state.mutation,
        [field]: value,
      } as EmployeeBankAccountMutationSTO,
    }));
  };

  return (
    <>
      <AddEditButton onClick={() => setIsDialogOpen(true)}>
        <>
          <FontAwesomeIcon icon={faChevronRight} /> &nbsp;
          <Typography variant={"subtitle2"}>
            {`${t(`${tRP}.common_keys.${hasMutation ? "edit_change_request" : "request_a_change"}`)}`}
          </Typography>
        </>
      </AddEditButton>
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={closeMainDialog}
      >
        <DialogTitle
          title={`${employee?.name}: ${t(`${tRP}.employee_data.employee_bank_account.edit_title`)}`}
          handleClose={closeMainDialog}
        />
        <DialogContent>
          <BankMutationInput
            originalValue={state.original?.name}
            mutationValue={state.mutation?.name}
            handleChangeMutation={handleChange}
            field="name"
          />
          <BankMutationInput
            originalValue={state.original?.description}
            mutationValue={state.mutation?.description}
            handleChangeMutation={handleChange}
            field="description"
          />
          <BankMutationInput
            originalValue={state.original?.iban}
            mutationValue={state.mutation?.iban}
            handleChangeMutation={handleChange}
            field="iban"
          />
          <BankMutationInput
            originalValue={state.original?.currency?.name}
            mutationValue={state.mutation?.currency?.name}
            handleChangeMutation={handleChange}
            mutationObject={state.mutation}
            field="currency"
          />
          <BankMutationInput
            originalValue={state.original?.toDate}
            mutationValue={state.mutation?.toDate}
            handleChangeMutation={handleChange}
            mutationObject={state.mutation}
            field="toDate"
          />
        </DialogContent>
        <DialogActions
          handleCancel={closeMainDialog}
          handleSave={handleSave}
          error={state.error}
          loading={state.loading}
          customSaveName={forManager ? `${t("buttons.approve")} & ${t("buttons.save")}` : `${t("buttons.save")}`}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isConfirmationChangesDialogOpen}
        handleCancel={() => setIsConfirmationChangesDialogOpen(false)}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditBankAccountMutation;
